import { usePlayer } from '@/features/player'
import { useT } from '@/internationalization/i18n'
import type { SummarySection } from '@/types'
import { sectionTime } from './sections'

/**
 * A summary's topics, each as a heading, the moment it began and a short body.
 *
 * Rendered nothing at all when there are none, so the caller's narrative is
 * what a summary without sections shows -- no empty heading, no placeholder.
 *
 * THE TIME IS A LINK ONLY WHERE THERE IS SOMETHING TO PLAY. `usePlayer` is
 * nullable and `isReady` is false for a meeting with no audio, so the shared
 * page (no player, ever) and a meeting whose recording is 404 or still loading
 * both get plain text -- the same rule `TranscriptPanel` applies, and for the
 * same reason: a control that does nothing when clicked is worse than one that
 * is plainly inert.
 *
 * A MISSING TIME IS AN EM DASH (STYLE-GUIDE: empty values are `—`), never
 * `0:00`, which would say the topic opened the meeting. Heading and body are
 * rendered as text children, so React escapes them.
 */
export function SummarySections({ sections }: { sections: SummarySection[] }) {
  const t = useT()
  const player = usePlayer()
  const playable = player?.isReady ? player : null

  if (sections.length === 0) return null

  return (
    <ol className="mt-4 space-y-4 border-t border-border pt-4">
      {sections.map((section, index) => {
        const time = sectionTime(section.timestampSeconds)
        return (
          <li key={`${index}-${section.heading}`}>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <h3 className="text-base font-semibold text-fg">{section.heading}</h3>
              {time === null ? (
                <span
                  className="font-mono text-xs text-fg-subtle"
                  aria-label={t('summary.sections.noTime')}
                >
                  —
                </span>
              ) : playable ? (
                <button
                  type="button"
                  onClick={() => playable.playFrom(section.timestampSeconds as number)}
                  className="font-mono text-xs tabular-nums text-fg-subtle transition-colors
                    hover:text-brand hover:underline"
                  aria-label={t('summary.sections.playFrom', { time })}
                >
                  ({time})
                </button>
              ) : (
                <span className="font-mono text-xs tabular-nums text-fg-subtle">({time})</span>
              )}
            </div>
            <p className="mt-1 whitespace-pre-line text-base leading-relaxed text-fg">{section.summary}</p>
          </li>
        )
      })}
    </ol>
  )
}
